import "./EditableModule.scss";
import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faPhone, faLockOpen, faLock, faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import EditButton from "../EditButton/EditButton";
import ClickBoundary from "../ClickBoundary/ClickBoundary";
import { useEditMode } from "../EditModeContext/EditModeContext";
import EditableSubmodule from "../EditableSubmodule/EditableSubmodule";
import { ContentType } from "../../ContentType";

const EditableModule = ({ module }) => {
	const [isVisible, setIsVisible] = useState(module.Visibility);
	const [isUnlocked, setIsUnlocked] = useState(module.Unlocked);
	const [typeValue, setTypeValue] = useState(module.Type ?? 0);

	const { activeField, setActiveField, cancelAll } = useEditMode();

	const [nameDraft, setNameDraft] = useState(module.Name);
	const [descDraft, setDescDraft] = useState(module.Description ?? "");

	const COLOUR_ORANGE = "#F68B1F";
	const COLOUR_BLUE = "#143872";
	const COLOUR_WHITE = "#FFFFFF";

	// Reset drafts whenever module data or global active field changes
	useEffect(() => {
		setNameDraft(module.Name);
		setDescDraft(module.Description ?? "");
	}, [module.Name, module.Description, activeField]);

	const handleNameKeyDown = (e) => {
		if (e.key === "Escape") {
			cancelAll();
		}
		else if (e.key === "Enter") {
			e.currentTarget.blur();
		}
	};

	const handleDescKeyDown = (e) => {
		if (e.key === "Escape") {
			cancelAll();
		}
		else if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			e.currentTarget.blur();
		}
	};

	const isEditingName = activeField?.scope === "module" && activeField.id === module.Id && activeField.field === "name";
	const isEditingDesc = activeField?.scope === "module" && activeField.id === module.Id && activeField.field === "desc";

	return (
		<div className="editable-module">
			<div className="editable-module-sidebar">
				{/* TYPE DROPDOWN */}
				<div className="editable-module-sidebar-item">
					<FontAwesomeIcon icon={faPhone} style={{ color: COLOUR_BLUE }} />
					<p>Type</p>
					<select
						key="ModuleTypeDropdown"
						value={typeValue}
						onChange={(e) => setTypeValue(Number(e.target.value))}
						className="editable-module-sidebar-dropdown"
					>
						<option value={ContentType.None}>None</option>
						<option value={ContentType.Device}>Device</option>
					</select>
				</div>

				{/* VISIBILITY TOGGLE */}
				<div className="editable-module-sidebar-item clickable" onMouseDown={(e) => e.stopPropagation()} onClick={() => setIsVisible((v) => !v)}>
					<FontAwesomeIcon icon={isVisible ? faEye : faEyeSlash} style={{ color: isVisible ? COLOUR_BLUE : COLOUR_ORANGE }} />
					<p>{isVisible ? "Visible" : "Hidden"}</p>
				</div>

				{/* UNLOCKED TOGGLE */}
				<div className="editable-module-sidebar-item clickable" onMouseDown={(e) => e.stopPropagation()} onClick={() => setIsUnlocked((v) => !v)}>
					<FontAwesomeIcon icon={isUnlocked ? faLockOpen : faLock} style={{ color: isUnlocked ? COLOUR_BLUE : COLOUR_ORANGE }} />
					<p>{isUnlocked ? "Unlocked" : "Locked"}</p>
				</div>
			</div>

			<div className="editable-module-main"> 
				<ClickBoundary>
					{isEditingName ? (
						<InputText
							autoFocus
							value={nameDraft}
							onChange={(e) => setNameDraft(e.target.value)}
							onKeyDown={handleNameKeyDown}
							className="w-full"
						/>
					) : (
						<h2 className="editable-module-title">
							{module.Name}
							<EditButton onClick={() => setActiveField({ scope: "module", id: module.Id, field: "name" })} />
						</h2>
					)}
				</ClickBoundary>

				<ClickBoundary>
					{isEditingDesc ? (
						<InputTextarea
							autoFocus
							value={descDraft}
							onChange={(e) => setDescDraft(e.target.value)}
							onKeyDown={handleDescKeyDown}
							rows={3}
							autoResize
							className="editable-module-main-description w-full"
						/>
					) : (
						<div className="editable-module-main-description">
							{module.Description}
							<EditButton onClick={() => setActiveField({ scope: "module", id: module.Id, field: "desc" })} />
						</div>
					)}
				</ClickBoundary>

				<h3>Submodules</h3>
				<div className="editable-submodules">
					{Array.isArray(module.Submodules) &&
						module.Submodules
						.slice()
						.sort((a, b) => (a.Position ?? 0) - (b.Position ?? 0))
							.map((sm) => (
								<EditableSubmodule
									key={sm.Id}
									moduleId={module.Id}
									moduleType={module.Type}
									submodule={sm}
								/>
							))}
				</div>

				<div className="editable-module-main-addnew">
					<div className="editable-module-main-addnew-button">
						<p>Add New Submodule <FontAwesomeIcon icon={faPlusCircle} style={{ color: COLOUR_WHITE }} /></p>
					</div>
					<div className="editable-module-main-addnew-button">
						<p>Add Exisiting Submodule <FontAwesomeIcon icon={faPlusCircle} style={{ color: COLOUR_WHITE }} /></p>
					</div>
					<div className="editable-module-main-addnew-button">
						<p>Add New Assessment <FontAwesomeIcon icon={faPlusCircle} style={{ color: COLOUR_WHITE }} /></p>
					</div>
				</div>

			</div>
		</div>
	);
};

export default EditableModule;
